import React from 'react';
import { Copy, FileText, FileSpreadsheet, FileDown } from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  pdf,
} from '@react-pdf/renderer';
import logoSiger from '../../../assets/logobgc.png';
import { captureChartsAsImages } from '../../../utils/chartToPdf';

const isTableSeparator = (line = '') => {
  const clean = line.trim();
  if (!clean.startsWith('|')) return false;

  const cells = clean
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());

  return cells.length > 0 && cells.every((cell) => /^:?-{1,}:?$/.test(cell));
};

const splitTableRow = (line = '') =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());

const cleanMarkdownText = (value = '') =>
  value
    .replace(/^[-*]\s+/, '• ')
    .replace(/\*\*/g, '')
    .replace(/__/g, '')
    .replace(/`/g, '')
    .trim();

const parseReportBlocks = (text = '') => {
  const sanitizedText = String(text)

  // eliminar bloque completo ARTIFACTS
  .replace(/\[ARTIFACTS\][\s\S]*?\[\/ARTIFACTS\]/gi, '')

  // eliminar tags sueltos
  .replace(/\[\/?ARTIFACTS\]/gi, '')

  // eliminar líneas relacionadas a artifacts
  .replace(/^.*artifact.*$/gim, '')
  .replace(/^.*artifacts.*$/gim, '')

  // eliminar "artes visuales"
  .replace(/^.*artes visuales.*$/gim, '')

  // eliminar headings vacíos tipo ## o ###
  .replace(/^\s*#{1,6}\s*$/gm, '')

  // eliminar separadores markdown raros
  .replace(/^\s*[-*_]{3,}\s*$/gm, '')

  // compactar saltos excesivos
  .replace(/\n{3,}/g, '\n\n')

  .trim();

  const lines = sanitizedText.split('\n');
  const blocks = [];
  let currentSection = null;
  let i = 0;

  const pushSection = () => {
    if (currentSection) {
      blocks.push(currentSection);
      currentSection = null;
    }
  };

  while (i < lines.length) {
    const line = lines[i];

    const headingMatch = line.match(/^#{1,4}\s+(.+)/);

    if (headingMatch) {
      pushSection();

      currentSection = {
        type: 'section',
        title: cleanMarkdownText(headingMatch[1]),
        items: [],
      };

      i++;
      continue;
    }

    const isTable =
      line.trim().startsWith('|') && isTableSeparator(lines[i + 1]);

    if (isTable) {
      const header = splitTableRow(lines[i]);
      const rows = [];

      i += 2;

      while (i < lines.length && lines[i].trim().startsWith('|')) {
        rows.push(splitTableRow(lines[i]));
        i++;
      }

      const tableBlock = {
        type: 'table',
        header,
        rows,
      };

      if (currentSection) {
        currentSection.items.push(tableBlock);
      } else {
        blocks.push(tableBlock);
      }

      continue;
    }

    if (line.trim()) {
      const textBlock = {
        type: 'text',
        value: cleanMarkdownText(line),
      };

      if (currentSection) {
        currentSection.items.push(textBlock);
      } else {
        blocks.push(textBlock);
      }
    }

    i++;
  }

  pushSection();

  return blocks;
};

const looksLikeKpiTable = (table) => {
  const firstHeader = table.header?.[0]?.toLowerCase() || '';
  const secondHeader = table.header?.[1]?.toLowerCase() || '';

  return firstHeader.includes('concepto') && secondHeader.includes('valor');
};

const normalizeSectionTitle = (title = '') =>
  String(title)
    .replace(/[📌📊🔎⚠️✅🧩]/g, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const getSectionVariant = (title = '') => {
  const normalized = normalizeSectionTitle(title);

  if (normalized.includes('resumen ejecutivo')) return 'executive';
  if (normalized.includes('kpis principales')) return 'kpis';
  if (normalized.includes('hallazgos')) return 'findings';
  if (normalized.includes('alertas') || normalized.includes('desvios')) return 'alerts';
  if (normalized.includes('recomendaciones')) return 'recommendations';
  if (normalized.includes('datos faltantes') || normalized.includes('supuestos')) return 'missing';

  return 'default';
};



const styles = StyleSheet.create({
  page: {
    paddingTop: 70,
    paddingHorizontal: 34,
    paddingBottom: 46,
    fontFamily: 'Helvetica',
    fontSize: 9.5,
    color: '#334155',
    backgroundColor: '#f8fafc',
  },

  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 46,
    backgroundColor: '#0f172a',
    paddingHorizontal: 34,
    flexDirection: 'row',
    alignItems: 'center',
  },

  logo: {
    width: 18,
    height: 18,
    marginRight: 10,
  },

  brand: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 700,
    marginRight: 16,
  },

  headerText: {
    color: '#e2e8f0',
    fontSize: 9,
  },

  footer: {
    position: 'absolute',
    bottom: 18,
    right: 34,
    color: '#64748b',
    fontSize: 8,
  },

  section: {
    border: '1 solid #dbe3ee',
    borderRadius: 9,
    padding: 13,
    marginBottom: 13,
    backgroundColor: '#ffffff',
  },

  sectionExecutive: {
    border: '1 solid #dbe3ee',
    borderRadius: 9,
    padding: 13,
    marginBottom: 13,
    backgroundColor: '#ffffff',
  },

  sectionKpis: {
    border: '1 solid #bfdbfe',
    borderRadius: 9,
    padding: 13,
    marginBottom: 13,
    backgroundColor: '#f8fbff',
  },

  sectionFindings: {
    border: '1 solid #fed7aa',
    borderLeft: '4 solid #f97316',
    borderRadius: 9,
    padding: 13,
    marginBottom: 13,
    backgroundColor: '#fffaf5',
  },

  sectionAlerts: {
    border: '1 solid #fcd34d',
    borderLeft: '4 solid #f59e0b',
    borderRadius: 9,
    padding: 13,
    marginBottom: 13,
    backgroundColor: '#fffbeb',
  },

  sectionRecommendations: {
    border: '1 solid #bbf7d0',
    borderLeft: '4 solid #10b981',
    borderRadius: 9,
    padding: 13,
    marginBottom: 13,
    backgroundColor: '#f0fdf4',
  },

  sectionMissing: {
    border: '1 solid #cbd5e1',
    borderLeft: '4 solid #64748b',
    borderRadius: 9,
    padding: 13,
    marginBottom: 13,
    backgroundColor: '#f8fafc',
  },

  sectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },

  sectionIcon: {
    width: 14,
    fontSize: 10,
    fontWeight: 700,
    color: '#0066cc',
    marginRight: 5,
  },

  sectionIconFindings: {
    color: '#f97316',
  },

  sectionIconAlerts: {
    color: '#f59e0b',
  },

  sectionIconRecommendations: {
    color: '#10b981',
  },

  sectionTitle: {
    fontSize: 12.5,
    fontWeight: 700,
    color: '#0f172a',
  },

  paragraph: {
    fontSize: 9.2,
    lineHeight: 1.5,
    marginBottom: 4.5,
    color: '#334155',
  },

  bulletParagraph: {
    fontSize: 9.2,
    lineHeight: 1.5,
    marginBottom: 4.5,
    color: '#334155',
    paddingLeft: 4,
  },

  table: {
    marginTop: 8,
    border: '1 solid #dbe3ee',
    borderRadius: 6,
    overflow: 'hidden',
  },

  tableRow: {
    flexDirection: 'row',
  },

  tableHeaderCell: {
    flex: 1,
    backgroundColor: '#14b8b8',
    paddingVertical: 7,
    paddingHorizontal: 6,
    fontSize: 7.5,
    fontWeight: 700,
    color: '#ffffff',
    borderRight: '1 solid #0f9f9f',
    borderBottom: '1 solid #0f9f9f',
  },

  tableCell: {
    flex: 1,
    paddingVertical: 7,
    paddingHorizontal: 6,
    fontSize: 7.5,
    lineHeight: 1.35,
    color: '#334155',
    borderRight: '1 solid #e5eaf0',
    borderBottom: '1 solid #e5eaf0',
    backgroundColor: '#ffffff',
  },

  kpiGrid: {
    marginTop: 8,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },

  kpiCard: {
    width: '31%',
    minHeight: 58,
    border: '1 solid #dbeafe',
    borderTop: '3 solid #0066cc',
    borderRadius: 7,
    padding: 9,
    marginBottom: 8,
    backgroundColor: '#f8fbff',
  },

  kpiLabel: {
    fontSize: 6.8,
    textTransform: 'uppercase',
    color: '#64748b',
    fontWeight: 700,
    marginBottom: 6,
    textAlign: 'center',
  },

  kpiValue: {
    fontSize: 13,
    color: '#0066cc',
    fontWeight: 700,
    textAlign: 'center',
  },

  introCard: {
    border: '1 solid #dbe3ee',
    borderRadius: 9,
    padding: 13,
    marginBottom: 13,
    backgroundColor: '#ffffff',
  },

  introTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: '#0f172a',
    marginBottom: 6,
  },

  introText: {
    fontSize: 9,
    lineHeight: 1.45,
    color: '#475569',
  },
});

const getPdfSectionStyle = (title = '') => {
  const variant = getSectionVariant(title);

  const map = {
    executive: styles.sectionExecutive,
    kpis: styles.sectionKpis,
    findings: styles.sectionFindings,
    alerts: styles.sectionAlerts,
    recommendations: styles.sectionRecommendations,
    missing: styles.sectionMissing,
    default: styles.section,
  };

  return map[variant] || styles.section;
};

const getPdfSectionMarkerStyle = (title = '') => {
  const variant = getSectionVariant(title);

  if (variant === 'executive') return [styles.sectionMarker, styles.sectionMarkerExecutive];
  if (variant === 'kpis') return [styles.sectionMarker, styles.sectionMarkerKpis];
  if (variant === 'findings') return [styles.sectionMarker, styles.sectionMarkerFindings];
  if (variant === 'alerts') return [styles.sectionMarker, styles.sectionMarkerAlerts];
  if (variant === 'recommendations') return [styles.sectionMarker, styles.sectionMarkerRecommendations];
  if (variant === 'missing') return [styles.sectionMarker, styles.sectionMarkerMissing];

  return styles.sectionMarker;
};
const isBulletText = (value = '') => {
  const clean = String(value).trim();
  return clean.startsWith('•') || clean.startsWith('-');
};

const PdfTable = ({ table }) => (
  <View style={styles.table}>
    <View style={styles.tableRow} wrap={false}>
      {table.header.map((cell, index) => (
        <Text key={index} style={styles.tableHeaderCell}>
          {cleanMarkdownText(cell)}
        </Text>
      ))}
    </View>

    {table.rows.map((row, rowIndex) => (
      <View key={rowIndex} style={styles.tableRow} wrap={false}>
        {table.header.map((_, cellIndex) => (
          <Text key={cellIndex} style={styles.tableCell}>
            {cleanMarkdownText(row[cellIndex] || '')}
          </Text>
        ))}
      </View>
    ))}
  </View>
);

const PdfKpiCards = ({ table }) => (
  <View style={styles.kpiGrid}>
    {table.rows.map((row, index) => (
      <View key={index} style={styles.kpiCard} wrap={false}>
        <Text style={styles.kpiLabel}>{cleanMarkdownText(row[0] || '')}</Text>
        <Text style={styles.kpiValue}>{cleanMarkdownText(row[1] || '')}</Text>
      </View>
    ))}
  </View>
);

const ReportPDF = ({ content, chartImages = [] }) => {
  const blocks = parseReportBlocks(content);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header} fixed>
          <Image src={logoSiger} style={styles.logo} />
          <Text style={styles.brand}>SIGER</Text>
          <Text style={styles.headerText}>Informe generado por IA</Text>
        </View>

        {blocks.map((block, index) => {
          if (block.type === 'section') {
  return (
    <View key={index} style={getPdfSectionStyle(block.title)} wrap={false}>
      <View style={styles.sectionTitleRow}>
        <View style={block.title} />
        <Text style={styles.sectionTitle}>{block.title}</Text>
      </View>

      {block.items.map((item, itemIndex) => {
        if (item.type === 'table') {
          return looksLikeKpiTable(item) ? (
            <PdfKpiCards key={itemIndex} table={item} />
          ) : (
            <PdfTable key={itemIndex} table={item} />
          );
        }

        return (
          <Text
            key={itemIndex}
            style={isBulletText(item.value) ? styles.bulletParagraph : styles.paragraph}
          >
            {item.value}
          </Text>
        );
      })}
    </View>
  );
}

          if (block.type === 'table') {
            return <PdfTable key={index} table={block} />;
          }

          return (
            <Text key={index} style={styles.paragraph}>
              {block.value}
            </Text>
          );
        })}

        {/* Incluir gráficos capturados si existen */}
        {chartImages.length > 0 && (
          <View style={{ marginTop: 20, break: true }}>
            <Text style={styles.sectionTitle}>Gráficos Analizados</Text>
            {chartImages.map((imageUrl, idx) => (
              <View key={idx} style={{ marginBottom: 16, alignItems: 'center' }}>
                <Image
                  src={imageUrl}
                  style={{
                    width: 500,
                    height: 250,
                    marginBottom: 8,
                  }}
                />
              </View>
            ))}
          </View>
        )}

        <Text
          style={styles.footer}
          render={({ pageNumber, totalPages }) =>
            `Página ${pageNumber} de ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
};

const AssistantActions = ({
  content = '',
  tables = [],
  charts = [],
  onCopy,
  copyFeedback = false,
}) => {
  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const element = document.createElement('a');

    element.href = url;
    element.download = filename;
    element.style.display = 'none';

    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);

    URL.revokeObjectURL(url);
  };

  const escapeHtml = (value = '') =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const downloadWord = () => {
    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Informe IA</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              color: #1f2937;
              line-height: 1.6;
            }
            pre {
              white-space: pre-wrap;
              font-family: Arial, sans-serif;
            }
          </style>
        </head>
        <body>
          <pre>${escapeHtml(content)}</pre>
        </body>
      </html>
    `;

    const blob = new Blob(['\ufeff', html], {
      type: 'application/msword;charset=utf-8',
    });

    downloadBlob(blob, `informe-ia-${Date.now()}.doc`);
  };

  const downloadExcel = () => {
    if (!tables.length) return;

    const workbook = XLSX.utils.book_new();

    tables.forEach((table, index) => {
      const rows = [table.header, ...table.rows];
      const worksheet = XLSX.utils.aoa_to_sheet(rows);

      const sheetName = `Tabla ${index + 1}`.slice(0, 31);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    XLSX.writeFile(workbook, `tablas-informe-${Date.now()}.xlsx`);
  };

  const downloadPDF = async () => {
    try {
      // Capturar gráficos como imágenes si existen
      let chartImages = [];
      if (charts && charts.length > 0) {
        chartImages = await captureChartsAsImages(charts, 800, 400);
      }

      // Generar PDF con las imágenes de gráficos
      const blob = await pdf(
        <ReportPDF content={content} chartImages={chartImages} />
      ).toBlob();

      downloadBlob(blob, `informe-asistente-${Date.now()}.pdf`);
    } catch (error) {
      console.error('Error generando PDF:', error);
      // Fallback: generar PDF sin gráficos
      const blob = await pdf(<ReportPDF content={content} />).toBlob();
      downloadBlob(blob, `informe-asistente-${Date.now()}.pdf`);
    }
  };

  return (
    <div className="assistant-card-actions">
      <button
        className="action-button copy-button"
        onClick={onCopy}
        title="Copiar contenido"
      >
        <Copy size={16} />
        <span>{copyFeedback ? 'Copiado' : 'Copiar'}</span>
      </button>

      <button
        className="action-button export-button"
        onClick={downloadWord}
        title="Descargar como Word"
      >
        <FileText size={16} />
        <span>Word</span>
      </button>

      <button
        className="action-button export-button"
        onClick={downloadPDF}
        title="Descargar como PDF"
      >
        <FileDown size={16} />
        <span>PDF</span>
      </button>

      {tables.length > 0 && (
        <button
          className="action-button export-button"
          onClick={downloadExcel}
          title="Descargar tablas en Excel"
        >
          <FileSpreadsheet size={16} />
          <span>Excel</span>
        </button>
      )}
    </div>
  );
};

export default AssistantActions;